/**
 * Types and formatters for the remediation detail page, which renders the task
 * payload from GET /remediation/:id/tasks. Kept apart from the component so the
 * shapes stay readable next to the table that consumes them.
 */

export interface SourceStatus {
  id: string;
  status: string;
  compliance_status: string;
  approval_status: string;
  implementation_percent: number;
  due_date: string | null;
}

export interface AssetSubTask {
  id: string;
  asset_id: string;
  status: string;
  compliance_status: string;
  asset?: { id: string; assetName: string; posture?: string };
}

export interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  due_date: string | null;
  notes: string | null;
  compliance_closed_at: string | null;
  orphaned_at: string | null;
  compliance_action_id: string | null;
  compliance?: { id: string; code: string; complianceItem_en: string };
  /**
   * No assignment fields: Supervisor and Manager belong to the whole remediation,
   * not to a task, and are shown once in the page header.
   */
  sourceStatus: SourceStatus | null;
  assetSubTasks: AssetSubTask[];
}

export interface Progress {
  total: number;
  done: number;
  inProgress: number;
  notStarted: number;
  percent: number;
  status: string;
}

/** The compliance vocabulary — 'Done', not 'Completed', is the closed state. */
export const TASK_STATUSES = ['Not Started', 'In Progress', 'Done'];

export const PRIORITIES = ['low', 'medium', 'high', 'critical'];

/** 'In Progress' -> 'in-progress', to match the CSS class names. */
export const statusSlug = (s: string) => (s || '').toLowerCase().replace(/\s+/g, '-');

export const statusClass = (s: string) => `rem-badge rem-status-${statusSlug(s)}`;

/**
 * Icon per status. The tone alone is not enough on a control that also has to
 * read as editable — and it is the one cue that survives a colour-blind reader
 * or a greyscale print of the page.
 */
export const statusIcon = (s: string) => {
  const v = statusSlug(s);
  if (v === 'done') return 'fa-check-circle';
  if (v === 'in-progress') return 'fa-clock-o';
  return 'fa-circle-o';
};

/**
 * Tone for the approval-stage badge. Stage names are configured per workflow in
 * the Workflow Builder, so they are matched by meaning rather than by an enum —
 * an unrecognised custom stage falls back to the neutral tone.
 */
export const workflowClass = (s?: string | null) => {
  const v = s || '';
  const tone = /publish/i.test(v)
    ? 'published'
    : /reject|refus/i.test(v)
    ? 'rejected'
    : /approv/i.test(v)
    ? 'approved'
    : /review|check/i.test(v)
    ? 'review'
    : 'draft';
  return `rem-badge rem-wf-${tone}`;
};

export const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '-');

export const fmtDateTime = (d: string | null) => (d ? new Date(d).toLocaleString() : '-');

/** Open task whose due date has passed — drives the overdue tag. */
export const isOverdue = (task: { due_date: string | null; status: string }) =>
  Boolean(task.due_date) && task.status !== 'Done' && new Date(task.due_date as string) < new Date();
